import './SearchPlaceMarker.scss';
import { useAtomValue, useSetAtom } from 'jotai';
import type { PopupOptions } from 'maplibre-gl';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/core/ui';
import { cx } from '@/core/utils';
import { MapMarker, MapPopup, useMap } from '@/features/map';
import { describeItem } from '../describeItem';
import { clearSearchSelectionAtom, selectionAtom } from '../state';
import type { SearchItem } from '../types';

const POPUP_OPTIONS: PopupOptions = { offset: 12, closeOnClick: false, maxWidth: '260px' };

interface Props {
  /** Popup button using the place, e.g. "Take off here"; without one the popup only describes it. */
  action?: { label: string; onAction: (item: SearchItem) => void };
}

/** Marker of the picked search result, with a popup offering to use it; closing the popup removes both. */
export function SearchPlaceMarker(props: Props) {
  const selection = useAtomValue(selectionAtom);
  if (!selection) return null;
  return <PlaceMarker key={selection.id} item={selection.item} {...props} />;
}

function PlaceMarker({ item, action }: Props & { item: SearchItem }) {
  const { t } = useTranslation('search');
  const map = useMap();
  const clearSelection = useSetAtom(clearSearchSelectionAtom);
  // The camera is usually flying to the item; open the popup once it arrives.
  const [open, setOpen] = useState(() => !map?.isMoving());

  useEffect(() => {
    if (!map?.isMoving()) return;
    const show = () => setOpen(true);
    map.once('moveend', show);
    return () => void map.off('moveend', show);
  }, [map]);

  // The popup is not attached via Marker.setPopup(): with 3D terrain on, MapLibre closes a marker's popup
  // (and refuses to reopen it) while the marker counts as hidden behind terrain, e.g. during the flight.
  return (
    <>
      <MapMarker lng={item.lng} lat={item.lat} className="search-dot" onClick={() => setOpen(true)} />
      <MapPopup lng={item.lng} lat={item.lat} open={open} options={POPUP_OPTIONS} onClose={clearSelection}>
        <h2 className="pr-8 text-[15px] leading-snug font-semibold">{item.name}</h2>
        <p className={cx('text-xs text-hint', action && 'mb-2')}>{describeItem(t, item)}</p>
        {action && (
          <Button
            onClick={() => {
              clearSelection();
              action.onAction(item);
            }}
          >
            {action.label}
          </Button>
        )}
      </MapPopup>
    </>
  );
}
